import { initMarketForms } from './prediction-ticket-ui.js';
function safeJson(value, fallback) {
    try { const parsed = JSON.parse(value || ''); return parsed ?? fallback; } catch (_) { return fallback; }
}

function escapeHtml(value) {
    return String(value).replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'",'&#039;');
}

function normalizePlayerSearch(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/\p{M}+/gu, '')
        .toLocaleLowerCase('cs-CZ')
        .replaceAll('ł', 'l')
        .replaceAll('ø', 'o')
        .replaceAll('đ', 'd')
        .replaceAll('ð', 'd')
        .replaceAll('þ', 'th')
        .replaceAll('æ', 'ae')
        .replaceAll('œ', 'oe')
        .replaceAll('ß', 'ss');
}


function positionFamily(value) {
    const p = String(value || '').toUpperCase();
    if (p.includes('GK')) return 'GK';
    if (p.includes('CB')) return 'CB';
    if (p.includes('LB') || p.includes('LWB')) return 'LB';
    if (p.includes('RB') || p.includes('RWB')) return 'RB';
    if (p.includes('DM')) return 'DM';
    if (p.includes('CM')) return 'CM';
    if (p.includes('AM')) return 'AM';
    if (p.includes('LW') || p.includes('LM')) return 'LW';
    if (p.includes('RW') || p.includes('RM')) return 'RW';
    if (p.includes('ST') || p.includes('CF') || p.includes('FW')) return 'ST';
    return p || 'ANY';
}

function fitScore(player, slot) {
    const p = positionFamily(player?.position);
    const s = positionFamily(slot?.positionCode || slot?.key);
    if (p === s) return 100;
    const close = {
        CB:['LB','RB','DM'], LB:['CB','LW'], RB:['CB','RW'], DM:['CM','CB'], CM:['DM','AM'], AM:['CM','LW','RW','ST'], LW:['AM','ST'], RW:['AM','ST'], ST:['AM','LW','RW']
    };
    return close[p]?.includes(s) ? 60 : (p === 'GK' || s === 'GK' ? -100 : 10);
}

function initLineupBuilders(root) {
    root.querySelectorAll('[data-v2-lineup-builder]').forEach((form) => {
        if (form.dataset.v2LineupReady === '1') return;
        form.dataset.v2LineupReady = '1';

        const formations = safeJson(form.dataset.formationsJson, []);
        const players = safeJson(form.dataset.playersJson, []);
        const initial = safeJson(form.dataset.initialJson, {});
        const initialSubstitutes = safeJson(form.dataset.initialSubstitutesJson, []);
        const playerBase = form.dataset.playerBase || '';
        const formationSelect = form.querySelector('[data-v2-formation-select]');
        const formationHidden = form.querySelector('[data-v2-formation-id]');
        const lineupHidden = form.querySelector('[data-v2-lineup-json]');
        const substitutesHidden = form.querySelector('[data-v2-substitutes-json]');
        const slotsRoot = form.querySelector('[data-v2-pitch-slots]');
        const countEl = form.querySelector('[data-v2-lineup-count]');
        const progressEl = form.querySelector('[data-v2-lineup-progress]');
        const submits = Array.from(form.querySelectorAll('[data-v2-lineup-submit]'));
        const saveSubmit = form.querySelector('[data-v2-lineup-save]');
        const pitch = form.querySelector('[data-v2-pitch]');
        const squadList = form.querySelector('[data-v2-squad-list]');
        const substituteCount = form.querySelector('[data-v2-substitute-count]');
        const readonly = pitch?.dataset.readonly === '1' || formationSelect?.disabled === true;
        const dialog = form.querySelector('[data-v2-player-dialog]');
        const dialogSlot = form.querySelector('[data-v2-dialog-slot]');
        const search = form.querySelector('[data-v2-player-search]');
        const playerList = form.querySelector('[data-v2-player-list]');
        const close = form.querySelector('[data-v2-dialog-close]');
        const trash = form.querySelector('[data-v2-lineup-trash]');
        const mobileActions = form.querySelector('[data-v2-mobile-actions]');
        const mobileAvatar = form.querySelector('[data-v2-mobile-player-avatar]');
        const mobileName = form.querySelector('[data-v2-mobile-player-name]');
        const mobileReplace = form.querySelector('[data-v2-mobile-replace]');
        const mobileRemove = form.querySelector('[data-v2-mobile-remove]');
        const mobileClose = form.querySelector('[data-v2-mobile-actions-close]');
        const desktopDragEnabled = window.matchMedia?.('(min-width: 768px) and (pointer: fine)').matches ?? false;
        if (!formationSelect || !formationHidden || !lineupHidden || !slotsRoot) return;

        const formationMap = new Map(formations.map((f) => [String(f.id), f]));
        const playerMap = new Map(players.map((p) => [String(p.id), p]));
        let selected = Object.fromEntries(Object.entries(initial).map(([slotId,playerId]) => [String(slotId), Number(playerId)]));
        let substitutes = new Set((Array.isArray(initialSubstitutes) ? initialSubstitutes : []).map(Number).filter((id) => Number.isInteger(id) && id > 0));
        let activeSlotId = null;
        let pickedSlotId = null;
        let dragSlotId = null;

        const photoUrl = (player) => player?.photo ? `${playerBase}${player.photo}` : '';
        const initials = (name) => String(name || '?').split(/\s+/).slice(0,2).map((x)=>x.charAt(0)).join('').toUpperCase();
        const availabilityMeta = (player) => {
            const map = {
                injured: ['Zraněný', 'text-rose-600 dark:text-rose-300'],
                doubtful: ['Nejistý start', 'text-amber-600 dark:text-amber-300'],
                suspended: ['Trest', 'text-rose-600 dark:text-rose-300'],
                ill: ['Nemoc', 'text-violet-600 dark:text-violet-300'],
                other: ['Omezení', 'text-slate-500 dark:text-slate-400'],
            };
            return map[player?.availabilityStatus] || null;
        };
        const availabilityHtml = (player) => {
            const meta = availabilityMeta(player);
            if (!meta) return '';
            const note = player?.availabilityNote ? ` · ${escapeHtml(player.availabilityNote)}` : '';
            return `<span class="mt-0.5 block text-[9px] font-bold ${meta[1]}"><i class="fa-duotone fa-solid fa-triangle-exclamation mr-1"></i>${meta[0]}${note}</span>`;
        };
        const playerAvatarHtml = (player, size='h-11 w-11', decoration='ring-1 ring-black/5 shadow-md shadow-black/20') => {
            const photo = photoUrl(player);
            const classes = `${decoration} transition duration-150`.trim();
            return photo
                ? `<span class="grid ${size} shrink-0 place-items-center overflow-hidden rounded-full bg-slate-900/15 ${classes}"><img src="${escapeHtml(photo)}" alt="" class="h-full w-full rounded-full object-cover object-top"></span>`
                : `<span class="grid ${size} shrink-0 place-items-center rounded-full bg-slate-700 text-[10px] font-bold text-white ${classes}">${escapeHtml(initials(player?.shortName || player?.name))}</span>`;
        };
        const selectedPlayerIds = () => new Set(Object.values(selected).map(String));
        const updateMobileActions = () => {
            if (!mobileActions) return;
            const sid = pickedSlotId != null ? String(pickedSlotId) : null;
            const player = sid ? playerMap.get(String(selected[sid])) : null;
            const visible = Boolean(player && !readonly);
            mobileActions.classList.toggle('hidden', !visible);
            if (!visible) return;
            if (mobileName) mobileName.textContent = player.shortName || player.name || 'Hráč';
            if (mobileAvatar) mobileAvatar.innerHTML = playerAvatarHtml(player, 'h-11 w-11', 'border-2 border-emerald-400/80 shadow-sm');
        };
        const syncHidden = () => {
            formationHidden.value = formationSelect.value || '';
            lineupHidden.value = JSON.stringify(selected);
            const starters = selectedPlayerIds();
            substitutes = new Set(Array.from(substitutes).filter((playerId) => !starters.has(String(playerId)) && playerMap.has(String(playerId))));
            if (substitutesHidden) substitutesHidden.value = JSON.stringify(Array.from(substitutes));
            const count = Object.keys(selected).length;
            if (countEl) countEl.textContent = `${count} / 11`;
            if (progressEl) progressEl.style.width = `${Math.min(100, Math.round(count/11*100))}%`;
            if (substituteCount) substituteCount.textContent = String(substitutes.size);
            submits.forEach((submit) => { submit.disabled = count !== 11 || !formationSelect.value; });
        };

        const setTrashVisible = (active) => {
            if (!trash) return;
            // The remove zone is intentionally always visible. Dragging only "wakes it up"
            // so the layout never jumps and the user always knows where removal lives.
            trash.classList.toggle('border-emerald-400', active);
            trash.classList.toggle('bg-emerald-50/70', active);
            trash.classList.toggle('text-emerald-700', active);
            trash.classList.toggle('shadow-md', active);
            trash.classList.toggle('dark:border-emerald-600', active);
            trash.classList.toggle('dark:bg-emerald-950/20', active);
            trash.classList.toggle('dark:text-emerald-300', active);
            if (!active) {
                trash.classList.remove('border-rose-400', 'bg-rose-50', 'text-rose-700', 'scale-[1.04]', 'shadow-lg', 'dark:border-rose-700', 'dark:bg-rose-950/25', 'dark:text-rose-200');
            }
        };

        const setDropTargets = (active, sourceSlotId = null) => {
            slotsRoot.querySelectorAll('[data-v2-slot-wrapper]').forEach((wrapper) => {
                const sid = wrapper.dataset.slotId || '';
                const target = wrapper.querySelector('[data-v2-slot-target]');
                const isSource = sourceSlotId != null && sid === String(sourceSlotId);
                wrapper.classList.toggle('z-30', active && !isSource);
                if (!target) return;
                target.classList.toggle('ring-2', active && !isSource);
                target.classList.toggle('ring-emerald-200/80', active && !isSource);
                target.classList.toggle('bg-emerald-300/10', active && !isSource);
                target.classList.toggle('scale-[1.04]', active && !isSource);
                target.classList.toggle('shadow-lg', active && !isSource);
            });
        };

        const setDropHover = (button, active) => {
            if (!button) return;
            button.classList.toggle('scale-110', active);
            button.classList.toggle('ring-4', active);
            button.classList.toggle('ring-emerald-300', active);
            button.classList.toggle('bg-emerald-300/20', active);
            button.classList.toggle('shadow-xl', active);
        };

        const removeFromSlot = (slotId) => {
            const sid = String(slotId || '');
            if (!sid || selected[sid] == null) return;
            delete selected[sid];
            if (pickedSlotId === sid) pickedSlotId = null;
            if (activeSlotId === sid) activeSlotId = null;
            renderPitch();
        };

        const swapOrMove = (fromSlot, toSlot) => {
            if (!fromSlot || !toSlot || fromSlot === toSlot || selected[fromSlot] == null) return;
            const moving = selected[fromSlot];
            const target = selected[toSlot];
            selected[toSlot] = moving;
            if (target != null) selected[fromSlot] = target; else delete selected[fromSlot];
            pickedSlotId = null;
            renderPitch();
        };

        const remapToFormation = (formation) => {
            const ids = Object.values(selected).map(Number);
            const slots = [...(formation?.slots || [])];
            const next = {};
            const remainingPlayers = ids.map((id, index) => ({id, index, player: playerMap.get(String(id))}));
            const remainingSlots = [...slots];
            while (remainingPlayers.length && remainingSlots.length) {
                let best = { score: -999, pi: 0, si: 0 };
                remainingPlayers.forEach((p,pi)=>remainingSlots.forEach((slot,si)=>{
                    const score = fitScore(p.player, slot) - p.index * 0.001;
                    if (score > best.score) best={score,pi,si};
                }));
                const p = remainingPlayers.splice(best.pi,1)[0];
                const slot = remainingSlots.splice(best.si,1)[0];
                next[String(slot.id)] = p.id;
            }
            selected = next;
            pickedSlotId = null;
        };

        const renderSquadList = () => {
            if (!squadList) return;
            const starters = selectedPlayerIds();
            squadList.innerHTML = '';

            // Zachovej stabilní pořadí soupisky. Přepnutí Náhradník/Zrušit náhradníka
            // mění pouze stav hráče, ne jeho pozici v seznamu, takže UI neposkakuje.
            const orderedPlayers = [...players];

            orderedPlayers.forEach((player) => {
                const playerId = Number(player.id);
                const isStarter = starters.has(String(playerId));
                const isSubstitute = substitutes.has(playerId) && !isStarter;

                const row = document.createElement('div');
                row.className = 'flex min-w-0 items-center gap-2 bg-white px-3 py-2.5';

                let actionHtml = '';
                if (isStarter) {
                    actionHtml = '<span class="shrink-0 rounded-md bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-700">Základ</span>';
                } else if (isSubstitute) {
                    actionHtml = '<button type="button" data-v2-remove-substitute class="shrink-0 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700 hover:bg-amber-100">Náhradník <i class="fa-solid fa-xmark ml-1"></i></button>';
                } else {
                    actionHtml = '<button type="button" data-v2-add-substitute class="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-50"><i class="fa-duotone fa-solid fa-user-plus mr-1"></i>Náhradník</button>';
                }

                row.innerHTML = `${playerAvatarHtml(player,'h-8 w-8')}<span class="min-w-0 flex-1"><b class="block truncate text-[11px] text-slate-700">${escapeHtml(player.shortName || player.name || '')}</b><small class="block truncate text-[9px] text-slate-400">${escapeHtml(player.position || '')}${player.shirtNumber!=null?` · #${player.shirtNumber}`:''}</small>${availabilityHtml(player)}</span>${actionHtml}`;

                const persistNomination = () => {
                    syncHidden();
                    renderSquadList();
                    if (!saveSubmit || saveSubmit.disabled || form.dataset.v2NominationSaving === '1') return;
                    form.dataset.v2NominationSaving = '1';
                    if (typeof form.requestSubmit === 'function') {
                        form.requestSubmit(saveSubmit);
                    } else {
                        saveSubmit.click();
                    }
                };

                row.querySelector('[data-v2-add-substitute]')?.addEventListener('click', () => {
                    if (readonly || starters.has(String(playerId))) return;
                    substitutes.add(playerId);
                    persistNomination();
                });
                row.querySelector('[data-v2-remove-substitute]')?.addEventListener('click', () => {
                    if (readonly) return;
                    substitutes.delete(playerId);
                    persistNomination();
                });

                squadList.appendChild(row);
            });
        };

        const renderPlayerList = () => {
            if (!playerList) return;
            const q = normalizePlayerSearch((search?.value || '').trim());
            const used = selectedPlayerIds();
            playerList.innerHTML = '';
            players.filter((player) => {
                if (!q) return true;
                const haystack = normalizePlayerSearch([
                    player.name,
                    player.shortName,
                    player.firstName,
                    player.lastName,
                    player.shirtNumber,
                    player.position,
                ].filter((value) => value != null && value !== '').join(' '));
                return haystack.includes(q);
            }).forEach((player)=>{
                const button=document.createElement('button'); button.type='button'; button.className='flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800';
                const usedElsewhere=used.has(String(player.id)) && String(selected[activeSlotId])!==String(player.id);
                button.innerHTML=`${playerAvatarHtml(player)}<span class="min-w-0 flex-1"><b class="block truncate text-sm">${escapeHtml(player.name||'')}</b><small class="text-slate-400">${escapeHtml(player.position||'')}${player.shirtNumber!=null?` · #${player.shirtNumber}`:''}</small>${availabilityHtml(player)}</span>${usedElsewhere?'<span class="text-[10px] font-bold uppercase text-slate-400">prohodit</span>':''}`;
                button.addEventListener('click',()=>{
                    if (activeSlotId==null) return;
                    const other=Object.keys(selected).find((sid)=>String(selected[sid])===String(player.id));
                    if (other && other!==activeSlotId) { const old=selected[activeSlotId]; selected[activeSlotId]=Number(player.id); if (old!=null) selected[other]=old; else delete selected[other]; }
                    else selected[activeSlotId]=Number(player.id);
                    dialog?.close(); pickedSlotId=null; renderPitch();
                });
                playerList.appendChild(button);
            });
        };

        const openPlayerDialog=(slot)=>{ if(readonly||!dialog)return; activeSlotId=String(slot.id); if(dialogSlot)dialogSlot.textContent=`${slot.label} · ${slot.positionCode||slot.key}`; if(search)search.value=''; renderPlayerList(); if(typeof dialog.showModal==='function')dialog.showModal(); else dialog.setAttribute('open',''); setTimeout(()=>search?.focus(),0); };

        const renderPitch=()=>{
            slotsRoot.innerHTML=''; const formation=formationMap.get(String(formationSelect.value));
            if(!formation){ selected={}; syncHidden(); renderSquadList(); return; }
            const allowed=new Set((formation.slots||[]).map((slot)=>String(slot.id))); Object.keys(selected).forEach((sid)=>{if(!allowed.has(sid))delete selected[sid];});
            (formation.slots||[]).forEach((slot)=>{
                const sid=String(slot.id);
                const wrapper=document.createElement('div');
                wrapper.className='group absolute -translate-x-1/2 -translate-y-1/2 overflow-visible transition-transform duration-150';
                wrapper.dataset.v2SlotWrapper='1';
                wrapper.dataset.slotId=sid;
                wrapper.style.left=`${slot.x}%`; wrapper.style.top=`${slot.y}%`;

                const button=document.createElement('button');
                button.type='button'; button.disabled=readonly; button.dataset.slotId=sid; button.dataset.v2SlotTarget='1';
                const isClickTarget = Boolean(pickedSlotId && pickedSlotId !== sid);
                button.className=`flex w-[4.9rem] flex-col items-center p-0 text-center text-white transition-all duration-150 disabled:cursor-default sm:w-[6.2rem] ${isClickTarget ? 'scale-[1.04]' : 'md:hover:scale-[1.03]'}`;
                const player=playerMap.get(String(selected[sid]));

                if(player){
                    button.draggable=!readonly && desktopDragEnabled;
                    const selectedRing = pickedSlotId===sid
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-emerald-800'
                        : '';
                    const glowClass = isClickTarget ? 'shadow-[0_0_0_2px_rgba(167,243,208,0.65)]' : '';
                    button.innerHTML=`
                        <span class="relative mx-auto block h-10 w-10 sm:h-14 sm:w-14">
                            ${playerAvatarHtml(player,'h-10 w-10 sm:h-14 sm:w-14',`${selectedRing} ${glowClass}`.trim())}
                        </span>
                        <span class="-mt-1 inline-flex max-w-full items-center gap-1 rounded-full bg-slate-950/80 px-1.5 py-0.5 text-[9px] font-bold text-white shadow-sm backdrop-blur-sm sm:text-[10px]">
                            ${player.shirtNumber!=null?`<span class="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-white/18 px-1 py-[1px] leading-none text-white/95">${player.shirtNumber}</span>`:''}
                            <span class="truncate">${escapeHtml(player.shortName||player.name||'')}</span>
                        </span>`;
                } else {
                    button.innerHTML=`<span class="grid h-11 w-11 place-items-center rounded-full border-2 border-dashed border-white/60 bg-slate-950/10 text-base sm:h-14 sm:w-14 sm:text-lg"><i class="fa-duotone fa-solid fa-user-plus"></i></span><span class="-mt-1 rounded-full bg-slate-950/55 px-1.5 py-0.5 text-[8px] font-bold uppercase">${escapeHtml(slot.positionCode||slot.key||'')}</span>`;
                }

                button.addEventListener('click',()=>{
                    if(readonly)return;
                    if(pickedSlotId){ if(pickedSlotId===sid){ pickedSlotId=null; renderPitch(); return; } swapOrMove(pickedSlotId,sid); return; }
                    if(player){ pickedSlotId=sid; renderPitch(); } else openPlayerDialog(slot);
                });
                button.addEventListener('dblclick',(e)=>{e.preventDefault(); if(player)openPlayerDialog(slot);});
                button.addEventListener('dragstart',(e)=>{
                    if(!player || readonly) return;
                    dragSlotId=sid;
                    setTrashVisible(true);
                    setDropTargets(true, sid);
                    if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed='move';
                        try { e.dataTransfer.setData('text/plain', sid); } catch (_) {}
                    }
                    requestAnimationFrame(()=>button.classList.add('opacity-45','scale-95'));
                });
                button.addEventListener('dragend',()=>{
                    dragSlotId=null;
                    button.classList.remove('opacity-45','scale-95');
                    setDropHover(button, false);
                    setDropTargets(false);
                    setTrashVisible(false);
                });
                button.addEventListener('dragenter',(e)=>{
                    if(!dragSlotId || dragSlotId===sid)return;
                    e.preventDefault();
                    setDropHover(button, true);
                });
                button.addEventListener('dragover',(e)=>{
                    if(!dragSlotId || dragSlotId===sid)return;
                    e.preventDefault();
                    if(e.dataTransfer)e.dataTransfer.dropEffect='move';
                    setDropHover(button, true);
                });
                button.addEventListener('dragleave',(e)=>{
                    if(e.relatedTarget && button.contains(e.relatedTarget))return;
                    setDropHover(button, false);
                });
                button.addEventListener('drop',(e)=>{
                    e.preventDefault();
                    setDropHover(button, false);
                    const from=dragSlotId; dragSlotId=null;
                    setDropTargets(false); setTrashVisible(false);
                    if(from && from!==sid)swapOrMove(from,sid);
                });
                wrapper.appendChild(button);

                if(player && !readonly){
                    const actions=document.createElement('div');
                    actions.className=`absolute left-1/2 top-0 z-40 hidden -translate-x-1/2 -translate-y-[45%] gap-1.5 overflow-visible transition duration-150 md:flex ${pickedSlotId===sid ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'}`;

                    const replace=document.createElement('button');
                    replace.type='button'; replace.title='Změnit hráče'; replace.setAttribute('aria-label','Změnit hráče');
                    replace.className='grid h-7 w-7 place-items-center rounded-full border border-white/40 bg-slate-950/85 text-[10px] text-white shadow-md transition-all duration-150 hover:scale-110 hover:rotate-12 hover:border-emerald-200 hover:bg-emerald-600 active:scale-95';
                    replace.innerHTML='<i class="fa-duotone fa-solid fa-arrows-rotate"></i>';
                    replace.addEventListener('click',(e)=>{e.preventDefault(); e.stopPropagation(); openPlayerDialog(slot);});

                    const remove=document.createElement('button');
                    remove.type='button'; remove.title='Odebrat hráče'; remove.setAttribute('aria-label','Odebrat hráče');
                    remove.className='grid h-7 w-7 place-items-center rounded-full border border-white/40 bg-slate-950/85 text-[10px] text-white shadow-md transition-all duration-150 hover:scale-110 hover:border-rose-200 hover:bg-rose-600 active:scale-95';
                    remove.innerHTML='<i class="fa-solid fa-xmark"></i>';
                    remove.addEventListener('click',(e)=>{e.preventDefault(); e.stopPropagation(); removeFromSlot(sid);});

                    actions.appendChild(replace); actions.appendChild(remove); wrapper.appendChild(actions);
                }

                slotsRoot.appendChild(wrapper);
            });
            syncHidden(); renderSquadList(); updateMobileActions();
        };

        mobileClose?.addEventListener('click', () => { pickedSlotId = null; renderPitch(); });
        mobileReplace?.addEventListener('click', () => {
            if (pickedSlotId == null) return;
            const formation = formationMap.get(String(formationSelect.value));
            const slot = (formation?.slots || []).find((item) => String(item.id) === String(pickedSlotId));
            if (slot) openPlayerDialog(slot);
        });
        mobileRemove?.addEventListener('click', () => {
            if (pickedSlotId == null) return;
            const sid = String(pickedSlotId);
            pickedSlotId = null;
            removeFromSlot(sid);
        });

        if (trash) {
            const setTrashHover = (active) => {
                trash.classList.toggle('border-rose-400', active);
                trash.classList.toggle('bg-rose-50', active);
                trash.classList.toggle('text-rose-700', active);
                trash.classList.toggle('scale-[1.04]', active);
                trash.classList.toggle('shadow-lg', active);
                trash.classList.toggle('dark:border-rose-700', active);
                trash.classList.toggle('dark:bg-rose-950/25', active);
                trash.classList.toggle('dark:text-rose-200', active);
                if (active) {
                    trash.classList.remove('border-emerald-400','bg-emerald-50/70','text-emerald-700','dark:border-emerald-600','dark:bg-emerald-950/20','dark:text-emerald-300');
                }
            };
            trash.addEventListener('dragenter',(e)=>{ if(!dragSlotId)return; e.preventDefault(); setTrashHover(true); });
            trash.addEventListener('dragover',(e)=>{ if(!dragSlotId)return; e.preventDefault(); if(e.dataTransfer)e.dataTransfer.dropEffect='move'; setTrashHover(true); });
            trash.addEventListener('dragleave',(e)=>{ if(e.relatedTarget && trash.contains(e.relatedTarget))return; setTrashHover(false); if(dragSlotId)setTrashVisible(true); });
            trash.addEventListener('drop',(e)=>{
                e.preventDefault();
                const sid=dragSlotId; dragSlotId=null;
                setDropTargets(false);
                if(sid) removeFromSlot(sid);
                setTrashHover(false);
                setTrashVisible(false);
            });
        }

        // Formation selector must stay independent from pitch selection/drag state.
        // Reset transient pitch interaction before the native select opens; otherwise a
        // previously picked player can make the next pointer interaction look like a
        // pitch swap instead of a formation change on some browsers/touch devices.
        const resetPitchInteraction = () => {
            pickedSlotId = null;
            activeSlotId = null;
            dragSlotId = null;
            setDropTargets(false);
            setTrashVisible(false);
            updateMobileActions();
        };
        formationSelect.addEventListener('pointerdown', resetPitchInteraction);
        formationSelect.addEventListener('mousedown', resetPitchInteraction);
        formationSelect.addEventListener('touchstart', resetPitchInteraction, { passive: true });
        formationSelect.addEventListener('change',()=>{
            resetPitchInteraction();
            const formation=formationMap.get(String(formationSelect.value));
            if(formation) remapToFormation(formation);
            renderPitch();
        });
        search?.addEventListener('input',renderPlayerList); close?.addEventListener('click',()=>dialog?.close()); dialog?.addEventListener('click',(e)=>{if(e.target===dialog)dialog.close();});
        renderPitch();
    });
}

export function initPredictionV2(root = document) {
    initMarketForms(root);
    initLineupBuilders(root);
}
